import React, { useState, useEffect, useRef } from "react";
import { func, string, object, bool, number, oneOfType } from "prop-types";
import {
  Col,
  Row,
  Form,
  FormGroup,
  FormLabel,
  Card,
  Alert,
} from "react-bootstrap";
import "!style-loader!css-loader!./styles.css";
import CreditCardInput from "react-credit-card-input";
import { getFormattedPrice } from "utils/shared";
import greyInfoIcon from "resources/images/info.svg";
import warning from "resources/images/warning-dark-red.png";
import AutoRenewalToggle from "containers/AccountBillingDetails/AutoRenewalToggle";
import OrderSummaryColumn from "./OrderSummaryColumn";

const PaymentPage = (props) => {
  const {
    handleRadio,
    handleOrderButton,
    handleInput,
    paymentMethod,
    shouldHideInvoice,
    toggleCheckbox,
    agreeToTerms,
    orderInProcess,
    contractPrice,
    taxAmount,
    paymentPageValidated,
    updatePaymentPageValidation,
    errorMessage,
    invalidCCInput,
    handleCCInputError,
    pricing,
    callLocation,
    allowPaymentPlan,
    showAutoRenewalToggle,
    allowUpdateAutoRenewal,
    handleUpdateAutoRenewal,
    isAutoRenewalEnabled,
    isAUSubsidiary,
    showAutoRenewalErrorMessage,
    showOrderDescription,
    showRingFencing,
    showParameter,
    currencyCode,
    showAutoRenewalText,
  } = props;

  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const [rightCardHeight, setRightCardHeight] = useState(0);

  useEffect(() => {
    if (rightCardRef.current) {
      setRightCardHeight(rightCardRef.current.offsetHeight);
    }
  }, []);

  // change the price to show in Subscription Price if payment plan is on
  const paymentPlanPrice = allowPaymentPlan
    ? (contractPrice / 3).toFixed(2)
    : 0;

  const formattedPaymentPlanPrice = getFormattedPrice(paymentPlanPrice);

  // this number is used to calculate the total payment due.
  const paymentAmount = !allowPaymentPlan ? contractPrice : paymentPlanPrice;

  const formattedContractPrice = getFormattedPrice(contractPrice);

  const taxAmountConsideringPaymentPlan = !allowPaymentPlan
    ? taxAmount
    : (taxAmount / 3).toFixed(2);

  const formattedTaxAmount = getFormattedPrice(taxAmountConsideringPaymentPlan);

  const formattedTotalAmount = getFormattedPrice(
    Number(paymentAmount) + Number(taxAmountConsideringPaymentPlan)
  );

  const handleOrderNowButtonClick = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || invalidCCInput) {
      e.stopPropagation();
      updatePaymentPageValidation(true);
    } else {
      handleOrderButton();
    }
  };

  const showInvoiceOption = !shouldHideInvoice();

  return (
    <div>
      <Form
        noValidate
        validated={paymentPageValidated}
        onSubmit={handleOrderNowButtonClick}
      >
        <div className="order-form-wrapper">
          <div className="form-column">
            <Card
              ref={leftCardRef}
              style={{ minHeight: `${rightCardHeight}px` }}
              className="p-4"
            >
              <Card.Body className="p-0">
                {showAutoRenewalErrorMessage ? (
                  <Alert variant="danger" className="d-flex">
                    <div>
                      <img
                        className="mr-3"
                        width="16px"
                        src={warning}
                        alt="caution sign"
                      />
                    </div>
                    <div>
                      Your subscription is currently on hold due to a payment
                      issue. Please review your billing and credit card details
                      to resume your subscription immediately. If this is
                      incorrect, please reach out to your Account Manager.
                    </div>
                  </Alert>
                ) : null}
                <div>
                  <p>Finish your payment details to complete your order.</p>
                  {showInvoiceOption ? (
                    <p>
                      Please note, the invoice may take up to 24 hours to be
                      sent to the email address associated to the {`'Bill To'`}{" "}
                      contact. If you do not receive it by then, please reach
                      out to your CSM.
                    </p>
                  ) : null}
                  <hr />
                </div>
                <Row>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <h4>Payment Method</h4>
                  </Col>
                </Row>
                <Row>
                  <Col lg={4} md={4} sm={4} xs={4}>
                    <FormGroup className="mt-3">
                      {showInvoiceOption && (
                        <Form.Check
                          type="radio"
                          checked={paymentMethod === "Invoice"}
                          name="paymentMethod"
                          id="Invoice"
                          onChange={handleRadio}
                          label="Invoice sent via email"
                        />
                      )}
                      {showInvoiceOption ? (
                        <Form.Check
                          type="radio"
                          name="paymentMethod"
                          id="Credit"
                          checked={paymentMethod === "Credit"}
                          onChange={handleRadio}
                          label="Credit Card"
                        />
                      ) : null}
                    </FormGroup>
                  </Col>
                </Row>
                {paymentMethod === "Credit" ? (
                  <div>
                    <FormGroup>
                      {paymentPageValidated ? (
                        <div style={{ color: "#dc3545" }}>
                          Please input credit card information
                        </div>
                      ) : (
                        <FormLabel>
                          Please input credit card information:
                        </FormLabel>
                      )}
                      <br />
                      <CreditCardInput
                        cardNumberInputProps={{
                          onChange: (event) => handleInput(event),
                          onError: handleCCInputError,
                          required: true,
                        }}
                        cardExpiryInputProps={{
                          onChange: (event) => handleInput(event),
                          onError: handleCCInputError,
                          required: true,
                        }}
                        cardCVCInputProps={{
                          onChange: (event) => handleInput(event),
                          onError: handleCCInputError,
                          required: true,
                        }}
                        fieldClassName="credit-card-input-field"
                        required
                      />
                    </FormGroup>
                    {errorMessage ? (
                      <Alert variant="danger">{errorMessage}</Alert>
                    ) : null}{" "}
                    <div className="d-flex">
                      <img
                        alt="info"
                        src={greyInfoIcon}
                        height="16px"
                        width="16px"
                        className="mr-2 align-self-center"
                      />

                      <div className="flex-grow-1">
                        By providing your credit card, you are authorizing
                        Criteria to charge the card on file.
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                <Row>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <FormGroup className="mt-3">
                      <Form.Check type="checkbox" id="agreeToTerms">
                        <Form.Check.Input
                          type="checkbox"
                          id="agree-to-terms"
                          checked={agreeToTerms}
                          onChange={toggleCheckbox}
                          required
                        />
                        <Form.Check.Label htmlFor="agree-to-terms">
                          I have read, accepted, and agreed to the{" "}
                          <a
                            href="https://www.criteriacorp.com/terms-use"
                            target="_blank"
                          >
                            Terms of Use
                          </a>
                          .
                        </Form.Check.Label>
                      </Form.Check>
                      <Form.Control.Feedback type="invalid">
                        Please accept the Terms of Use.
                      </Form.Control.Feedback>
                    </FormGroup>
                  </Col>
                </Row>
                {showAutoRenewalToggle ? (
                  <AutoRenewalToggle
                    allowUpdateAutoRenewal={allowUpdateAutoRenewal}
                    handleUpdateAutoRenewal={handleUpdateAutoRenewal}
                    isAutoRenewalEnabled={Boolean(isAutoRenewalEnabled)}
                    showCannotUpdateAutoRenewalMessage={false}
                    updatingAutoRenewal={false}
                  />
                ) : null}
              </Card.Body>
            </Card>
          </div>
          <div className="order-summary-column">
            <Card ref={rightCardRef} className="p-4">
              <OrderSummaryColumn
                allowPaymentPlan={allowPaymentPlan}
                buttonText="Order Now"
                callLocation={callLocation}
                formattedContractPrice={formattedContractPrice}
                formattedPaymentPlanPrice={formattedPaymentPlanPrice}
                formattedTotalAmount={formattedTotalAmount}
                isAUSubsidiary={isAUSubsidiary}
                pricing={pricing}
                showLoadingButton={orderInProcess}
                taxAmountToShow={formattedTaxAmount}
                showOrderDescription={showOrderDescription}
                showRingFencing={showRingFencing}
                showParameter={showParameter}
                currencyCode={currencyCode}
                showAutoRenewalText={showAutoRenewalText}
              />
            </Card>
          </div>
        </div>
      </Form>
    </div>
  );
};

PaymentPage.propTypes = {
  handleRadio: func.isRequired,
  handleOrderButton: func.isRequired,
  handleInput: func.isRequired,
  paymentMethod: string,
  shouldHideInvoice: func.isRequired,
  toggleCheckbox: func.isRequired,
  agreeToTerms: bool.isRequired,
  orderInProcess: bool.isRequired,
  taxAmount: oneOfType([string, number]),
  contractPrice: oneOfType([string, number]),
  paymentPageValidated: bool.isRequired,
  updatePaymentPageValidation: func.isRequired,
  errorMessage: string,
  invalidCCInput: bool,
  handleCCInputError: func.isRequired,
  pricing: object,
  callLocation: string.isRequired,
  allowPaymentPlan: number,
  showAutoRenewalToggle: bool,
  allowUpdateAutoRenewal: bool,
  handleUpdateAutoRenewal: func,
  isAutoRenewalEnabled: bool,
  isAUSubsidiary: bool,
  showAutoRenewalErrorMessage: bool,
  showOrderDescription: bool,
  showRingFencing: bool,
  showParameter: bool,
  currencyCode: string,
  showAutoRenewalText: bool,
};

export default PaymentPage;
